<template>
	<div
		class="hdl-multiple-choice question"
		role="radiogroup"
		:aria-labelledby="`${question.uuid}-label`"
		:id="`${question.uuid}-container`"
		:class="{
			'has-error': errorMap.size > 0
		}"
		@keydown="keydownHandler"
		v-show="question.visible"
	>
		<fieldset>
			<legend>
				<div
					ref="rendered"
					v-html="question.renderedQuestion"
				/>
			</legend>

			<div class="errors" :id="`errs-${question.uuid}`" v-if="errorMessages.length" aria-live="assertive">
				<ul>
					<li v-for="errorMessage of errorMessages" :key="errorMessage">
						{{ errorMessage }}
					</li>
				</ul>
			</div>

			<div
				class="choices mb-2"
				ref="container"
			>
				<div
					v-for="choice, index of choices"
					:class="{'selected': isSelected(choice.uuid), 'has-text-option': choice.hasTextOption}"
					class="choice"
					ref="choices"
					:data-uuid="choice.uuid"
					@click.prevent.stop="selectChoice(choice.uuid)"
				>
					<div
						class="checkbox-control"
					>
						<div
							class="mr-2 flex-none checkbox-container"
						>
							<checkbox-indicator
								:selected="isSelected(choice.uuid)"
								:with-native="{
									name: question.uuid, id: choice.uuid, labelledBy: `${choice.uuid}-label`
								}"
								@checkbox:change="checkboxChanged($event, choice)"
							/>
						</div>
						<label
							class="flex flex-col flex-1"
							:id="`${choice.uuid}-label`"
							:for="choice.uuid"
						>
							<div class="choice-label">{{ choice.label }}</div>
						</label>
					</div>
					<div class="choice-other" v-if="choice.hasTextOption">
						<hdl-textbox
							class="w-full choice-text"
							:model-value="choicesTextMap[choice.uuid]"
							@update:model-value="handleChoiceTextboxInput(choice, $event)"
							@key-up-out="handleChoiceTextboxMovement(choice, 'up')"
							@key-down-out="handleChoiceTextboxMovement(choice, 'down')"
							@escape="handleChoiceTextboxMovement(choice, 'escape')"
							@enter="handleChoiceTextboxMovement(choice,'enter')"
							@click.stop="() => {}"
							:aria-label="`Extra text input for choice ${choice.label}`"
							capture-keys
						/>
					</div>
				</div>
			</div>
		</fieldset>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { PublishedQuestionChoiceType, PublishedSurveyQuestionType, QuestionChoiceBadge, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';

import { IQuestionComponent } from './types';
import { ChoiceResponse, ResponseGroup } from '../../responses';
import { useResponseStore } from '@/respondants/stores/responseStore';
import { inPlaceShuffleArray } from '@/common/utils';

@Component({
	emits: ['intentionalBlur']
})
export default class MultipleChoice extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	response: Array<string> | null;

	selectedChoices: Array<string> = [];

	errorMap: Map<SurveyResponseErrorCode, string> = new Map();

	choicesTextMap: {[choiceUuid: string]: string | null} = {};

	mounted(): void {
		this.$el.__vue__ = this;

		this.errorMap = new Map();

		this.selectedChoices = [];

		this.choicesTextMap = {};

		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			if (existingResponse && existingResponse.choices && existingResponse.choices.length) {
				for (const existingSelectedChoice of existingResponse.choices) {
					this.selectedChoices.push(existingSelectedChoice.choiceUuid);
					if (existingSelectedChoice.text) {
						this.choicesTextMap[existingSelectedChoice.choiceUuid] = existingSelectedChoice.text;
					}
				}
			}
		}

		this.accessorizeRendered();
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		const choices = this.$refs.choices as Array<HTMLDivElement> | undefined;

		if (choices === undefined) {
			return true;
		}

		if (fromDirection === 'below' && choices.length > 0) {
			choices[this.choices.length - 1].focus();
		} else if (choices.length > 0) {
			choices[0].focus();
		}

		return true;
	}

	keydownHandler(event: KeyboardEvent): void {
		const focusedUuid = this.focusedDomChoiceUuid();

		const focusedIndex = (focusedUuid !== null)?
				this.choices.findIndex(
					(c: PublishedQuestionChoiceType) => c.uuid === focusedUuid
				)
			:
				null;

		if (event.key === 'ArrowDown') {
			event.stopPropagation();
			event.preventDefault();

			const currentIndex = focusedIndex;

			if (currentIndex !== null && currentIndex < this.choices.length - 1) {
				const nextChoice = (this.$refs.choices as Array<HTMLDivElement>)[currentIndex + 1];

				if (nextChoice) {
					(nextChoice.querySelector('input[type="checkbox"]')! as HTMLInputElement).focus();
				}
			} else if (currentIndex === this.choices.length - 1) {
				this.$emit('intentionalBlur', this.$el, this.question, 'down');
			}
		} else if (event.key == 'ArrowUp') {
			event.stopPropagation();
			event.preventDefault();

			const currentIndex = focusedIndex;

			if (currentIndex !== null && currentIndex > 0) {
				const nextChoice = (this.$refs.choices as Array<HTMLDivElement>)[currentIndex - 1];
				if (nextChoice) {
					(nextChoice.querySelector('input[type="checkbox"]')! as HTMLInputElement).focus();
				}
			} else if (currentIndex === 0) {
				this.$emit('intentionalBlur', this.$el, this.question, 'up');
			}
		} else if (event.key === 'ArrowRight') {
			const currentIndex = focusedIndex;

			if (currentIndex !== null && currentIndex > -1) {
				const choiceEl = this.sortedChoiceEls[currentIndex];
				const inputEl = choiceEl.querySelector('input[type="text"]') as HTMLInputElement;

				if (inputEl) {
					inputEl.focus();
				}
			}
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.stopPropagation();
			event.preventDefault();

			if (focusedUuid !== null) {
				this.selectChoice(focusedUuid);
			}

			if (event.key === 'Enter') {
				this.$emit('intentionalBlur', this.$el, this.question, 'down');
			} else if (event.key === ' ') {
				if (
					focusedIndex !== null
					&& focusedIndex > -1
					&& focusedUuid
					&& this.selectedChoices.length > 0
					&& this.selectedChoices.includes(focusedUuid)
				) {
					const inputEl = this.sortedChoiceEls[focusedIndex].querySelector('input[type="text"]') as HTMLInputElement;

					if (inputEl) {
						inputEl.focus();
					}
				}
			}
		}
	}

	setResponse(): void {
		if (this.responseGroup) {
			let choices = [];
			for (const choiceUuid of this.selectedChoices) {
				const text = (choiceUuid in this.choicesTextMap)? this.choicesTextMap[choiceUuid] : null;
				choices.push({ choiceUuid, text });
			}

			this.responseGroup.setResponse(
				this.question.uuid,
				null,
				null,
				choices,
				null
			);
		}
	}

	handleChoiceTextboxInput(choice: PublishedQuestionChoiceType, newValue: string): void {
		this.choicesTextMap[choice.uuid] = (newValue.trim().length > 0)? newValue : null;

		if (!this.selectedChoices.includes(choice.uuid)) {
			this.selectedChoices.push(choice.uuid);
		}
		this.setResponse();
	}

	handleChoiceTextboxMovement(choice: PublishedQuestionChoiceType, movement: 'up' | 'down' | 'escape' | 'enter') {
		if (movement === 'up') {
			const choiceIndex = this.choices.findIndex((c: PublishedQuestionChoiceType) => c.uuid === choice.uuid);

			const targetChoiceIndex = (choiceIndex < 0)? 0 : choiceIndex - 1;

			if (targetChoiceIndex <= 0) {
				this.$emit('intentionalBlur', this.$el, this.question, 'up');
			} else {
				const nextChoice = this.sortedChoiceEls[targetChoiceIndex];

				if (nextChoice) {
					nextChoice.focus();
				}
			}
		} else if (movement === 'down') {
			const choiceIndex = this.choices.findIndex((c: PublishedQuestionChoiceType) => c.uuid === choice.uuid);

			const targetChoiceIndex = (choiceIndex < 0)? 0 : choiceIndex + 1;

			if (targetChoiceIndex >= this.choices.length - 1) {
				this.$emit('intentionalBlur', this.$el, this.question, 'down');
			} else {
				const nextChoice = this.sortedChoiceEls[targetChoiceIndex];

				if (nextChoice) {
					nextChoice.focus();
				}
			}
		} else if (movement === 'escape') {

		} else if (movement === 'enter') {
			this.selectChoice(choice.uuid);
			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		}
	}

	focusedDomChoiceUuid(): string | null {
		const container = this.$refs.container as HTMLDivElement | undefined;

		if (container === undefined) {
			return null;
		}

		if (document.activeElement && container.contains(document.activeElement)) {
			return document.activeElement.getAttribute('id');
		}

		return null;
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);
			label.classList.add('question-label');

			if (this.question.required) {
				const sroRequiredIndicator = document.createElement('span');
				sroRequiredIndicator.classList.add('hdl-sr-only');
				sroRequiredIndicator.textContent = '(required)';
				labelTail.appendChild(sroRequiredIndicator);
				const requiredIndicator = document.createElement('div');
				requiredIndicator.setAttribute('aria-hidden', 'true');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	checkLimits(): void {
		if (this.question.limitMin === null || this.question.limitMin === 0 || this.selectedChoices.length >= this.question.limitMin) {
			this.clearError(SurveyResponseErrorCode.UNDER_MINIMUM);
		}

		if (this.question.limitMax === null || this.question.limitMax === 0 || this.selectedChoices.length <= this.question.limitMax) {
			this.clearError(SurveyResponseErrorCode.OVER_LIMIT);
		}
	}

	checkboxChanged(event: InputEvent, choice: PublishedQuestionChoiceType): void {
		if (this.isSelected(choice.uuid)) {
			this.deselectChoice(choice.uuid);
		} else {
			this.selectChoice(choice.uuid);
		}
	}

	deselectChoice(choiceUuid: string): void {
		const selectedIndex = this.selectedChoices.findIndex(
			(scu: string) => scu === choiceUuid
		);

		if (selectedIndex < 0) {
			return;
		}

		this.selectedChoices.splice(selectedIndex, 1);

		this.checkLimits();

		if (this.responseGroup) {
			this.responseGroup.setResponse(this.question.uuid, null, null, this.selectedChoices.map((choiceUuid: string) => ({choiceUuid})), null);
		}
	}

	selectChoice(choiceUuid: string): void {
		this.clearError(SurveyResponseErrorCode.MISSING_REQUIRED);

		if (this.isSelected(choiceUuid)) {
			this.deselectChoice(choiceUuid);
			return;
		}

		this.selectedChoices.push(choiceUuid);

		this.checkLimits();

		this.setResponse();
	}

	isSelected(choiceUuid: string): boolean {
		return this.selectedChoices.includes(choiceUuid);
	}

	getResponse(): Array<string> | null {
		return this.response;
	}

	hasResponse(): boolean {
		return this.response !== null;
	}

	clearError(errorCode: SurveyResponseErrorCode): void {
		this.errorMap.delete(errorCode);
	}

	markError(error: SurveyResponseInputErrorType): void {
		this.errorMap.set(error.code, error.error);
	}

	get errorMessages(): Array<string> {
		return Array.from(this.errorMap.values());
	}

	get sortedChoiceEls(): Array<HTMLDivElement> {
		return (this.$refs.choices as Array<HTMLDivElement>).sort(
			//@ts-ignore
			(a, b) => parseInt(a.getAttribute('data-index')) - parseInt(b.getAttribute('data-index'))
		);
	}

	get choices(): Array<PublishedQuestionChoiceType> {
		if (this.question === null) {
			return [];
		}

		if (!this.question.randomizeChoices) {
			return this.question.choices;
		}

		const toShuffle: Array<PublishedQuestionChoiceType> = [...this.question.choices].filter((pqc: PublishedQuestionChoiceType) => !pqc.isPinned);
		inPlaceShuffleArray<PublishedQuestionChoiceType>(toShuffle);

		const output: Array<PublishedQuestionChoiceType> = [];

		for (const placedChoice of this.question.choices) {
			if (placedChoice.isPinned) {
				output.push(placedChoice);
			} else {
				output.push(toShuffle.pop()!);
			}
		}

		return output;
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}
}
</script>

<style lang="scss">
.hdl-multiple-choice {
	@apply relative;

	&.has-error {
		label {
			@apply text-hdl-wc-red;
		}
	}

	.errors {
		@apply text-hdl-wc-red text-sm;

		ul {
			list-style: none;
			margin: 0;
			padding: 0;

			li {
				margin: 0;
				list-style-type: none;
			}
		}
	}

	.choices {
		.choice {
			@apply px-2 rounded mb-2 cursor-pointer select-none border border-transparent p-1;

			min-width: 100%;
			width: 100%;

			.checkbox-control {
				@apply flex flex-row items-start;
			}

			&:hover {
				background: var(--srv-hover-fill);
			}

			&.selected {
				background: var(--srv-accent-fill);
			}

			&.has-text-option {
				.choice-label {
					@apply pb-1;
				}
			}

			&:focus {
				.checkbox-indicator {
					border: 1px solid white;
					box-shadow: 0 0 0 1px black;
					outline: none;
				}
				outline: none;
			}

			.hdl-textbox.choice-text {
				border-color: var(--srv-border);
				background-color: var(--srv-background);
				input {
					background-color: var(--srv-background);
				}
			}

			min-height: 32px;

			.checkbox-container {
				padding-top: 2px;
			}

			.choice-other {
				padding-left: 28px;
			}

			.hdl-textbox.choice-text {
				border-color: var(--srv-border);
				background-color: var(--srv-background);

				&:focus-within {
					outline: none;
					--tw-border-opacity: 1;
					border: 1.5px solid var(--main);
					box-shadow: #535abb25 0 0 0 2px;
				}

				input {
					background-color: var(--srv-background);
				}
			}

			label {
				@apply font-normal cursor-pointer;
				font-size: 1rem;
			}

			.choice-label {
				padding-top: 0px;
			}
		}
	}
}
</style>
